import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon, ArrowLeftIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { stripHtml as sanitizeStripHtml } from '../../utils/sanitize';
import {
  fetchAllKpiDrilldownTickets,
  fetchKpiDrilldownTickets,
  type KpiDrilldownSpec,
  type KpiDrilldownTicketDto,
  type KpiDrilldownSlaCellDto,
} from './kpiDashboardApi';
import { kpiResilienceCrossQueryKey } from './kpiResilienceCrossFilter';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useClientExportFlow } from '../../hooks/useClientExportFlow';
import {
  type TicketType,
  priorityLabels,
  priorityColors,
  stateLabels,
  changeStateLabels,
  problemStateLabels,
  ticketTypeLabels,
  slaStatusLabels,
  slaStatusColors,
  channelLabels,
  closureCodeLabels,
} from '../dashboard/types/pulseReport';
import {
  KPI_DRILLDOWN_SLA_GROUP_ONGOING,
  KPI_DRILLDOWN_SLA_GROUP_RESOLUTION,
  KPI_DRILLDOWN_SLA_GROUP_RESPONSE,
  KPI_DRILLDOWN_SLA_SUBTH_ONGOING,
  KPI_DRILLDOWN_SLA_SUBTH_RESOLUTION,
  KPI_DRILLDOWN_SLA_SUBTH_RESPONSE,
  KPI_DRILLDOWN_SLA_TD_ONGOING,
  KPI_DRILLDOWN_SLA_TD_RESOLUTION,
  KPI_DRILLDOWN_SLA_TD_RESPONSE,
  KPI_DRILLDOWN_TH,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';
import { exportKpiDrilldownTicketsCsv, kpiDrilldownFileSlug } from './kpiDrilldownTicketsExport';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
/** 10 base + 15 SLA + 21 post-SLA (Pulse) */
const TABLE_COL_COUNT = 46;

const formatDuration = (minutes: number | null | undefined): string => {
  if (minutes === null || minutes === undefined) return '-';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const label = priorityLabels[priority] || priority;
  const colorClass = priorityColors[priority] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
      {label}
    </span>
  );
};

const StateBadge: React.FC<{ state: string; ticketType: TicketType }> = ({ state, ticketType }) => {
  let label = state;

  if (ticketType === 'PROBLEM') {
    label = problemStateLabels[state] || stateLabels[state] || state;
  } else if (ticketType === 'CHANGE') {
    label = changeStateLabels[state] || stateLabels[state] || state;
  } else {
    label = stateLabels[state] || state;
  }

  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      {label}
    </span>
  );
};

const SLAStatusBadge: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status || status === 'N/A') return <span className="text-gray-400 text-xs">-</span>;

  const label = slaStatusLabels[status] || status;
  const colorClass = slaStatusColors[status] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${colorClass}`}>{label}</span>
  );
};

function ticketDetailPath(t: KpiDrilldownTicketDto): string {
  switch (t.ticketType) {
    case 'INCIDENT':
      return `/incidents/${t.id}`;
    case 'SERVICE_REQUEST':
      return `/service-requests/${t.id}`;
    case 'CASE':
      return `/cases/${t.id}`;
    case 'CHANGE':
      return `/changes/${t.id}`;
    case 'PROBLEM':
      return `/problems/${t.id}`;
    default:
      return `/tickets/${t.id}`;
  }
}

function handleRowOpen(t: KpiDrilldownTicketDto) {
  const routes: Partial<Record<TicketType, string>> = {
    INCIDENT: `/incidents/${t.id}`,
    PROBLEM: `/problems/${t.id}`,
    CHANGE: `/changes/${t.id}`,
    SERVICE_REQUEST: `/service-requests/${t.id}`,
    CASE: `/cases/${t.id}`,
  };
  const route = routes[t.ticketType as TicketType] ?? ticketDetailPath(t);
  window.open(route, '_blank', 'noopener,noreferrer');
}

function SlaGroupCells({
  sla,
  groupBg,
  borderRight,
  hide,
  formatDateTime,
}: {
  sla: KpiDrilldownSlaCellDto;
  groupBg: 'response' | 'ongoing' | 'resolution';
  borderRight?: boolean;
  hide: boolean;
  formatDateTime: (v: string | null | undefined) => string;
}) {
  const rowBg =
    groupBg === 'response'
      ? KPI_DRILLDOWN_SLA_TD_RESPONSE
      : groupBg === 'ongoing'
        ? KPI_DRILLDOWN_SLA_TD_ONGOING
        : KPI_DRILLDOWN_SLA_TD_RESOLUTION;
  const rightBorder = borderRight ? 'border-r border-slate-200' : '';

  if (hide) {
    return (
      <>
        {[0, 1, 2, 3, 4].map((i) => (
          <td
            key={i}
            className={`border border-slate-200 px-2 py-3 text-center text-xs text-slate-400 ${rowBg} ${i === 4 ? rightBorder : ''}`}
          >
            —
          </td>
        ))}
      </>
    );
  }

  const reasonRaw = sla.breachReason ? sanitizeStripHtml(sla.breachReason).trim() : '';
  const reasonDisplay = reasonRaw || '-';

  return (
    <>
      <td className={`border border-slate-200 px-2 py-3 text-center text-xs text-slate-700 ${rowBg} whitespace-nowrap`}>
        {formatDateTime(sla.startTime)}
      </td>
      <td className={`border border-slate-200 px-2 py-3 text-center text-xs text-slate-700 ${rowBg} whitespace-nowrap`}>
        {formatDateTime(sla.targetTime)}
      </td>
      <td className={`border border-slate-200 px-2 py-3 text-center ${rowBg}`}>
        <SLAStatusBadge status={sla.status} />
      </td>
      <td className={`border border-slate-200 px-2 py-3 text-center text-xs text-slate-700 ${rowBg} whitespace-nowrap`}>
        {formatDateTime(sla.breachedAt)}
      </td>
      <td
        className={`max-w-[160px] truncate border border-slate-200 px-2 py-3 text-center text-xs text-red-600 ${rowBg} ${rightBorder}`}
        title={reasonRaw || undefined}
      >
        {reasonDisplay}
      </td>
    </>
  );
}

function KpiExtendedTicketCells({
  t,
  formatDateTime,
}: {
  t: KpiDrilldownTicketDto;
  formatDateTime: (v: string | null | undefined) => string;
}) {
  const tt = t.ticketType as TicketType;
  const showChangeCols = tt === 'CHANGE';
  const showClosureCode = tt !== 'INCIDENT' && tt !== 'SERVICE_REQUEST';
  const showProblemCols = tt === 'PROBLEM';

  const tdBase = 'border border-slate-200 px-4 py-3 text-sm text-slate-900';
  const td = (node: React.ReactNode, extra = '') => (
    <td className={`${tdBase} whitespace-nowrap ${extra}`.trim()}>{node}</td>
  );
  const tdDash = () => (
    <td className={`${tdBase} text-center text-xs text-slate-400`}>—</td>
  );

  const holdClean = t.onHoldReason ? sanitizeStripHtml(t.onHoldReason).trim() : '';
  const closureClean = t.ticketClosureRemarks ? sanitizeStripHtml(t.ticketClosureRemarks).trim() : '';

  return (
    <>
      {td(t.createdBy || '-')}
      {td(formatDateTime(t.ticketLastUpdatedTime))}
      {td(t.lastUpdatedBy || '-')}
      {td(t.channel ? channelLabels[t.channel] || t.channel : '-')}
      {td(t.resource || '-')}
      {td(formatDateTime(t.ticketCreated))}
      {td(formatDateTime(t.ticketResolved))}
      {td(t.ticketResolvedBy || '-')}
      {td(formatDateTime(t.ticketClosed))}
      {td(t.ticketClosedBy || '-')}
      <td className={`${tdBase} max-w-[192px]`}>
        <div className="truncate" title={holdClean || undefined}>
          {holdClean || '-'}
        </div>
      </td>
      <td className={`${tdBase} max-w-[192px]`}>
        <div className="truncate" title={closureClean || undefined}>
          {closureClean || '-'}
        </div>
      </td>
      {td(formatDuration(t.ticketDurationMins))}
      {showChangeCols
        ? td(t.changeStatus ? changeStateLabels[t.changeStatus] || t.changeStatus : '-')
        : tdDash()}
      {showChangeCols ? td(formatDateTime(t.plannedStartDate)) : tdDash()}
      {showChangeCols ? td(formatDateTime(t.plannedEndDate)) : tdDash()}
      {showChangeCols ? td(formatDateTime(t.actualStartDate)) : tdDash()}
      {showChangeCols ? td(formatDateTime(t.actualEndDate)) : tdDash()}
      {showClosureCode
        ? td(t.closureCode ? closureCodeLabels[t.closureCode] || t.closureCode : '-')
        : tdDash()}
      {showProblemCols ? td(t.rcaPresentedBy || '-') : tdDash()}
      {showProblemCols ? td(formatDateTime(t.problemDate)) : tdDash()}
    </>
  );
}

export interface KpiDrilldownTicketsPanelProps {
  drill: KpiDrilldownSpec | null;
  filterValues: Record<string, string | string[]>;
  filterKey: string;
  onClose: () => void;
  /** Optional Resilience cross-filter (Resilience tab only). */
  resilienceCross?: import('./kpiResilienceCrossFilter').KpiResilienceCrossSlice;
  /** Fills the KPI dashboard main content area (charts hidden). Default: true. */
  layout?: 'overlay' | 'inline';
  /** Label for the primary back action (e.g. Heatmap (Customer) vs main charts). */
  backButtonLabel?: string;
}

const KpiDrilldownTicketsPanel: React.FC<KpiDrilldownTicketsPanelProps> = ({
  drill,
  filterValues,
  filterKey,
  onClose,
  resilienceCross,
  layout = 'overlay',
  backButtonLabel = 'Back to charts',
}) => {
  const { formatDateTime: formatDateTimeBase } = useDateFormatter();
  const formatDateTime = useCallback(
    (value: string | null | undefined) => formatDateTimeBase(value),
    [formatDateTimeBase]
  );

  const timezoneOffset = useMemo(() => new Date().getTimezoneOffset(), []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { run, modal } = useClientExportFlow();
  const resilienceKey = useMemo(() => kpiResilienceCrossQueryKey(resilienceCross ?? null), [resilienceCross]);

  useEffect(() => {
    setPage(1);
  }, [drill, filterKey, pageSize]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['kpi-drilldown', filterKey, drill, resilienceKey, page, pageSize],
    queryFn: () =>
      fetchKpiDrilldownTickets(filterValues, timezoneOffset, drill!, page, pageSize, resilienceCross ?? null),
    enabled: drill !== null,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!drill || layout !== 'overlay') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drill, layout, onClose]);

  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const isOverlay = layout === 'overlay';

  const handleExportDrilldownCsv = useCallback(() => {
    if (!drill || total === 0) return;
    void run({
      downloadButtonLabel: 'Download CSV',
      work: async () => {
        const tickets = await fetchAllKpiDrilldownTickets(
          filterValues,
          timezoneOffset,
          drill,
          resilienceCross ?? null
        );
        if (tickets.length === 0) {
          throw new Error('No tickets to export for this drill-down.');
        }
        const filterLabel = data?.filterLabel ?? 'Drill-down';
        const files = exportKpiDrilldownTicketsCsv(
          tickets,
          formatDateTime,
          `kpi_dashboard_${kpiDrilldownFileSlug(filterLabel)}`
        );
        return {
          files,
          summaryLines: [
            `${tickets.length.toLocaleString()} row(s) ready.`,
            'Your CSV file is ready to download.',
          ],
        };
      },
    });
  }, [run, data?.filterLabel, drill, filterValues, formatDateTime, resilienceCross, timezoneOffset, total]);

  if (!drill) return null;

  return (
    <>
      {modal}
      <section
      role={isOverlay ? 'dialog' : undefined}
      aria-modal={isOverlay ? true : undefined}
      aria-label="KPI drill-down tickets"
      className={
        isOverlay
          ? 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-lg shadow-slate-900/5 ring-1 ring-slate-950/[0.04]'
          : 'mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow'
      }
    >
      <div className="flex shrink-0 flex-col gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-lg font-medium text-gray-900">
            Showing: {data?.filterLabel ?? '…'}
            {total > 0 ? ` (${total} tickets)` : !isLoading ? ' (0 tickets)' : ''}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            Same columns as the Pulse report table: SLA groups (dashes for Change and Problem), then audit, lifecycle,
            and type-specific fields.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {isOverlay && (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <ArrowLeftIcon className="mr-1.5 h-4 w-4" aria-hidden />
              {backButtonLabel}
            </button>
          )}
          {total > 0 && (
            <button
              type="button"
              onClick={handleExportDrilldownCsv}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:px-3 sm:py-1.5 sm:text-sm"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0 text-slate-600 sm:h-4 sm:w-4" aria-hidden />
              Export CSV
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100"
            aria-label="Close drill-down"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      </div>

      {isLoading && (
        <div
          className={`flex flex-col items-center justify-center text-gray-500 ${
            isOverlay ? 'min-h-[12rem] flex-1' : 'py-16'
          }`}
        >
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent" />
          <p className="mt-3 text-sm">Loading tickets…</p>
        </div>
      )}

      {error && (
        <div className="m-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error instanceof Error ? error.message : 'Failed to load tickets.'}
        </div>
      )}

      {data && !isLoading && (
        <div className={isOverlay ? 'flex min-h-0 flex-1 flex-col' : ''}>
          <div
            className={
              isOverlay
                ? 'min-h-0 flex-1 overflow-x-auto overflow-y-auto'
                : 'max-h-96 overflow-x-auto'
            }
          >
            <table className="min-w-full border-collapse border border-slate-200">
              <thead className="sticky top-0 z-20 bg-[#e0f2fe] shadow-[0_1px_0_0_rgba(148,163,184,0.35)]">
                <tr className="border-b border-slate-200">
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Task Type
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} sticky left-0 z-10 px-4 py-2 align-middle`}
                  >
                    Number
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} sticky left-[108px] z-10 px-4 py-2 align-middle shadow-[2px_0_5px_-2px_rgba(15,23,42,0.08)]`}
                  >
                    Short Description
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Type
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Priority
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    State
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Assignment Group
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Assigned To
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Company
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Customer Contact
                  </th>
                  <th colSpan={5} className={KPI_DRILLDOWN_SLA_GROUP_RESPONSE}>
                    Response SLA
                  </th>
                  <th colSpan={5} className={KPI_DRILLDOWN_SLA_GROUP_ONGOING}>
                    Ongoing SLA
                  </th>
                  <th colSpan={5} className={KPI_DRILLDOWN_SLA_GROUP_RESOLUTION}>
                    Resolution SLA
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Created By
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Last Updated Time
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Last Updated By
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Channel
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Resource
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Ticket Created
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Ticket Resolved
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Resolved By
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Ticket Closed
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Closed By
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    On Hold Reason
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Closure Remarks
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Ticket Duration (Mins)
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Change Status
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Planned Start Date
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Planned End Date
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Actual Start Date
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Actual End Date
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Closure Code
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    RCA Presented By
                  </th>
                  <th
                    rowSpan={2}
                    className={`${KPI_DRILLDOWN_TH} px-4 py-2 align-middle`}
                  >
                    Problem Date
                  </th>
                </tr>
                <tr>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESPONSE}>Start</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESPONSE}>Target</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESPONSE}>Status</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESPONSE}>Breached At</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESPONSE}>Reason</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_ONGOING}>Start</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_ONGOING}>Target</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_ONGOING}>Status</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_ONGOING}>Breached At</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_ONGOING}>Reason</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESOLUTION}>Start</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESOLUTION}>Target</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESOLUTION}>Status</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESOLUTION}>Breached At</th>
                  <th className={KPI_DRILLDOWN_SLA_SUBTH_RESOLUTION}>Reason</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {data.tickets.length === 0 ? (
                  <tr>
                    <td
                      colSpan={TABLE_COL_COUNT}
                      className="border border-slate-200 px-4 py-8 text-center text-sm text-slate-500"
                    >
                      No tickets found matching the filter criteria.
                    </td>
                  </tr>
                ) : (
                  data.tickets.map((t, rowIndex) => {
                    const hideSla = t.ticketType === 'CHANGE' || t.ticketType === 'PROBLEM';
                    const taskTypeLabel =
                      ticketTypeLabels[t.ticketType as TicketType] || t.taskTypeLabel;
                    const descClean = t.shortDescription
                      ? sanitizeStripHtml(t.shortDescription).trim() || ''
                      : '';

                    return (
                      <tr
                        key={t.id}
                        className={`${kpiDashboardTableRowClass(rowIndex)} cursor-pointer`}
                        onClick={() => handleRowOpen(t)}
                      >
                        <td className="whitespace-nowrap border border-slate-200 px-4 py-3 text-sm text-slate-900">
                          {taskTypeLabel}
                        </td>
                        <td className="sticky left-0 z-10 whitespace-nowrap border border-slate-200 bg-inherit px-4 py-3 text-sm">
                          <span className="font-medium text-[#2F5597]">{t.number}</span>
                        </td>
                        <td
                          className="sticky left-[108px] z-10 max-w-xs border border-slate-200 bg-inherit px-4 py-3 text-sm text-slate-900 shadow-[2px_0_5px_-2px_rgba(15,23,42,0.08)]"
                          title={descClean || undefined}
                        >
                          <div className="truncate">{descClean || '-'}</div>
                        </td>
                        <td className="whitespace-nowrap border border-slate-200 px-4 py-3 font-mono text-sm text-slate-900">
                          {t.typeDisplay}
                        </td>
                        <td className="border border-slate-200 px-4 py-3">
                          <PriorityBadge priority={t.priority} />
                        </td>
                        <td className="whitespace-nowrap border border-slate-200 px-4 py-3">
                          <StateBadge state={t.state} ticketType={t.ticketType as TicketType} />
                        </td>
                        <td className="border border-slate-200 px-4 py-3 text-sm text-slate-900">
                          {t.assignmentGroup || '-'}
                        </td>
                        <td className="whitespace-nowrap border border-slate-200 px-4 py-3 text-sm text-slate-900">
                          {t.assignedTo || '-'}
                        </td>
                        <td className="border border-slate-200 px-4 py-3 text-sm text-slate-900">
                          {t.company || '-'}
                        </td>
                        <td className="border border-slate-200 px-4 py-3 text-sm text-slate-900">
                          {t.customerContact || '-'}
                        </td>
                        <SlaGroupCells
                          sla={t.responseSla}
                          groupBg="response"
                          borderRight
                          hide={hideSla}
                          formatDateTime={formatDateTime}
                        />
                        <SlaGroupCells
                          sla={t.ongoingSla}
                          groupBg="ongoing"
                          borderRight
                          hide={hideSla}
                          formatDateTime={formatDateTime}
                        />
                        <SlaGroupCells
                          sla={t.resolutionSla}
                          groupBg="resolution"
                          hide={hideSla}
                          formatDateTime={formatDateTime}
                        />
                        <KpiExtendedTicketCells t={t} formatDateTime={formatDateTime} />
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div
            className={`flex flex-col gap-2 border-t border-slate-200 bg-slate-50/90 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${
              isOverlay ? 'shrink-0' : ''
            }`}
          >
            {total > 0 ? (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size, index) => {
                      const previousSize = index > 0 ? PAGE_SIZE_OPTIONS[index - 1]! : 0;
                      const isDisabled = previousSize >= total && size > total;
                      return (
                        <option key={size} value={size} disabled={isDisabled}>
                          {size}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>
                <div className="text-center text-sm text-gray-700">
                  {isFetching && !isLoading ? (
                    <span className="text-indigo-600">Updating…</span>
                  ) : (
                    <>
                      Showing {Math.min((page - 1) * pageSize + 1, total)} to{' '}
                      {Math.min(page * pageSize, total)} of {total} entries
                    </>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-center gap-1 sm:justify-end">
                  <button
                    type="button"
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="First page"
                  >
                    &laquo;
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {(() => {
                    const pages: (number | string)[] = [];
                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else if (page <= 3) {
                      pages.push(1, 2, 3, 4, '...', totalPages);
                    } else if (page >= totalPages - 2) {
                      pages.push(
                        1,
                        '...',
                        totalPages - 3,
                        totalPages - 2,
                        totalPages - 1,
                        totalPages
                      );
                    } else {
                      pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
                    }
                    return pages.map((pNum, idx) =>
                      pNum === '...' ? (
                        <span key={`e-${idx}`} className="px-2 py-1 text-sm text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pNum}
                          type="button"
                          onClick={() => setPage(pNum as number)}
                          className={`rounded-md border px-3 py-1 text-sm ${
                            page === pNum
                              ? 'border-indigo-600 bg-indigo-600 text-white'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pNum}
                        </button>
                      )
                    );
                  })()}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="rounded-md border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage(totalPages)}
                    disabled={page >= totalPages}
                    className="rounded-md border border-gray-300 px-2 py-1 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Last page"
                  >
                    &raquo;
                  </button>
                </div>
              </>
            ) : (
              <div className="w-full text-center text-sm text-gray-500">No records</div>
            )}
          </div>
        </div>
      )}
    </section>
    </>
  );
};

export default KpiDrilldownTicketsPanel;
